import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// POST /api/admin/tiers/programs/:id/set-default
//
// Pessimistic. Default-flip has cascading effects:
//   - the prior default's row gets is_default=false (transactionally in the
//     service)
//   - the 0038 tg_sync_partner_legacy_tier trigger now mirrors a different
//     program's tier into partners.tier / partners.tier_id, which changes
//     every partner card's resolved tier badge
//
// Hence the broad `partners.all` invalidation alongside the tier-specific
// invalidations. We don't optimistically flip a local `is_default` because
// the prior default also needs to flip and the list refetch is authoritative.
export function useSetDefaultProgram() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, { id: string }>({
    mutationFn: ({ id }) =>
      apiFetch<{ ok: true }>(`/admin/tiers/programs/${id}/set-default`, {
        method: "POST",
      }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.programs.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
      // Phase 7 sub-bucket 13 carry-over fix. The admin dashboard's
      // "Default program" / "Active programs" widgets resolve off the
      // default flag; without this invalidate the dashboard renders stale
      // until the user navigates away and back.
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.dashboard() });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to set default program");
    },
  });
}
